import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';

import type { AccountBody, TransferBody, TransferStatus } from '@saldo/contracts';

import { describeSaveState, needsReconciliation, type SaveState } from './save';
import type { PairSuggestionView, PaymentForm, PaymentPreviewView, PaymentRateMode } from './payments';
import { applySuggestion, paymentTimingLabel } from './payments';
import { formatBalance, formatMoney, formatMoneyMagnitude } from './money';

/**
 * The payment panel (P6-T2).
 *
 * One panel serves the three whole-pair operations the payer performs on a pair of accounts:
 * **create** a payment, **complete** a plan on a chosen date, and **cancel** a pair. Each one
 * is preview-then-commit, and the preview is what the panel renders — with **both accounts'
 * impacts** side by side, each as `before → effect → after`, before anything is written. That
 * is the review the plan asks for: not a confirmation of a request, but the two account
 * figures the committed pair will produce.
 *
 * The panel decides no money. Every figure it shows comes from `payments.ts`, which reads the
 * API's own recorded balances and the pair's own legs, and the position sentence comes from the
 * same signed figure the card label is derived from — so the words cannot disagree with the
 * numbers.
 *
 * The form's state lives here, which is what makes a draft survive an allowed failure: a refusal
 * or an unknown outcome leaves the panel mounted with every field exactly as typed, and the
 * "reconcile this intent" path retries the *same* operation key. Nothing is written to
 * browser storage.
 */

/** Which whole-pair operation the panel is performing. */
export type PaymentMode = 'create' | 'complete' | 'cancel';

export interface PaymentPanelProps {
  readonly accounts: readonly AccountBody[];
  /** The account the ledger is showing, used only to seed the two selects. */
  readonly defaultSourceAccountId: string;
  readonly defaultDestinationAccountId: string;
  /** The server's business day, used only as a date default. */
  readonly businessToday: string;
  /** The pair being completed or canceled; `null` for a creation. */
  readonly transfer: TransferBody | null;
  readonly mode: PaymentMode;
  readonly saveState: SaveState;
  readonly saveMessage: string;
  readonly localError: string | null;
  /** The `Idempotency-Key` the last attempt carried, and whether it was a reuse. */
  readonly operationKey: { readonly key: string; readonly reused: boolean } | null;
  /** The stored preview awaiting confirmation, or `null` before one was taken. */
  readonly preview: PaymentPreviewView | null;
  readonly previewError: string | null;
  /** The pairs the owner has already used, offered for explicit reuse (`TRF-06`); possibly empty. */
  readonly suggestions: readonly PairSuggestionView[];
  readonly busy: boolean;
  onReviewCreate(form: PaymentForm): void;
  onReviewComplete(transferDate: string): void;
  onReviewCancel(): void;
  onConfirm(): void;
  /** Retry the retained intent with its original operation key (an unknown outcome). */
  onReconcile(): void;
  onClose(): void;
}

function firstOr(value: string, candidates: readonly AccountBody[]): string {
  if (candidates.some((account) => account.id === value)) return value;
  return candidates[0]?.id ?? '';
}

export function PaymentPanel(props: PaymentPanelProps) {
  const { accounts, businessToday, mode, transfer } = props;
  const formId = useId();

  const cashAccounts = useMemo(() => accounts.filter((account) => account.kind === 'cash'), [accounts]);

  const [form, setForm] = useState<PaymentForm>(() => ({
    sourceAccountId: firstOr(props.defaultSourceAccountId, cashAccounts),
    destinationAccountId: firstOr(
      props.defaultDestinationAccountId,
      accounts.filter((account) => account.id !== firstOr(props.defaultSourceAccountId, cashAccounts)),
    ),
    amount: '',
    description: '',
    transferDate: businessToday,
    status: 'completed',
    rateMode: 'same',
    rate: '',
    destinationAmount: '',
  }));
  const [completionDate, setCompletionDate] = useState(transfer?.transferDate ?? businessToday);

  const chosenSource = accounts.find((account) => account.id === form.sourceAccountId);
  const chosenDestination = accounts.find((account) => account.id === form.destinationAccountId);
  /**
   * Whether the chosen pair crosses two currencies — the condition that makes the mode's operand
   * required (P7-T3). It is derived from the two accounts the user picked, never from a rate.
   */
  const crosses =
    chosenSource !== undefined &&
    chosenDestination !== undefined &&
    chosenSource.currency !== chosenDestination.currency;

  // The panel follows the pair it was opened for: opening another pair replaces the draft.
  const openedFor = useRef(`${transfer?.id ?? 'new'}:${mode}`);
  useEffect(() => {
    const key = `${transfer?.id ?? 'new'}:${mode}`;
    if (key === openedFor.current) return;
    openedFor.current = key;
    setCompletionDate(transfer?.transferDate ?? businessToday);
    setForm((current) => ({
      ...current,
      transferDate: businessToday,
    }));
  }, [transfer, mode, businessToday]);

  const pending = props.saveState === 'saving' || props.busy;
  const locked = props.saveState === 'unknown';
  const reconcile = needsReconciliation(props.saveState);

  function update(patch: Partial<PaymentForm>): void {
    setForm((current) => ({ ...current, ...patch }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (pending || locked) return;
    if (mode === 'create') props.onReviewCreate(form);
    else if (mode === 'complete') props.onReviewComplete(completionDate);
    else props.onReviewCancel();
  }

  const title =
    mode === 'create' ? 'New payment' : mode === 'complete' ? 'Complete payment' : 'Cancel payment';
  const reviewLabel =
    mode === 'create' ? 'Review payment' : mode === 'complete' ? 'Review completion' : 'Review cancellation';

  return (
    <div className="editor" data-testid="payment-panel" data-mode={mode}>
      <button
        type="button"
        className="editor-backdrop"
        aria-label="Close the payment panel"
        data-testid="payment-backdrop"
        onClick={props.onClose}
        disabled={pending}
      />
      <form className="editor-panel" onSubmit={handleSubmit} noValidate>
        <header>
          <h2 id={`${formId}-title`} data-testid="payment-title">
            {title}
          </h2>
          <button type="button" data-testid="payment-close" onClick={props.onClose} disabled={pending}>
            Close
          </button>
        </header>

        {mode === 'create' ? (
          <div className="editor-fields">
            {props.suggestions.length > 0 ? (
              <div className="field" data-testid="payment-suggestions">
                <span className="muted">Pairs you have used — applied only when you choose one</span>
                <ul className="preview-list" data-testid="payment-suggestion-list">
                  {props.suggestions.map((suggestion) => (
                    <li key={`${suggestion.sourceAccountId}:${suggestion.destinationAccountId}`}>
                      <button
                        type="button"
                        className="link"
                        data-testid="payment-suggestion"
                        data-rate-mode={suggestion.rateMode}
                        disabled={pending || locked}
                        onClick={() => update(applySuggestion(suggestion))}
                      >
                        {suggestion.label}
                      </button>{' '}
                      <span className="muted" data-testid="payment-suggestion-detail">
                        {suggestion.detail}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <label className="field">
              From (cash)
              <select
                data-testid="payment-source"
                value={form.sourceAccountId}
                disabled={pending || locked}
                onChange={(event) => update({ sourceAccountId: event.target.value })}
              >
                {cashAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name} · {account.currency}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              To
              <select
                data-testid="payment-destination"
                value={form.destinationAccountId}
                disabled={pending || locked}
                onChange={(event) => update({ destinationAccountId: event.target.value })}
              >
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name} · {account.kind} · {account.currency}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              Amount
              <input
                data-testid="payment-amount"
                inputMode="decimal"
                autoComplete="off"
                placeholder="0.00"
                value={form.amount}
                disabled={pending || locked}
                onChange={(event) => update({ amount: event.target.value })}
              />
            </label>

            {/*
              The cross-currency operands (P7-T3). They appear only when the two chosen accounts
              disagree on currency: a same-currency pair has no rate to state, and the form offers
              no way to state one — which is what "no implicit parity" looks like on the surface.
            */}
            {crosses ? (
              <div className="two-fields" data-testid="payment-fx">
                <label className="field">
                  State
                  <select
                    data-testid="payment-fx-mode"
                    value={form.rateMode}
                    disabled={pending || locked}
                    onChange={(event) => update({ rateMode: event.target.value as PaymentRateMode })}
                  >
                    <option value="rate">The rate</option>
                    <option value="destination">The arriving amount</option>
                    <option value="same">Same currency (no rate)</option>
                  </select>
                </label>
                {form.rateMode === 'rate' ? (
                  <label className="field">
                    Rate —{' '}
                    {chosenSource?.currency} → {chosenDestination?.currency}
                    <input
                      data-testid="payment-fx-rate"
                      inputMode="decimal"
                      autoComplete="off"
                      placeholder="e.g. 5.4"
                      value={form.rate}
                      disabled={pending || locked}
                      onChange={(event) => update({ rate: event.target.value })}
                    />
                  </label>
                ) : form.rateMode === 'destination' ? (
                  <label className="field">
                    Arriving amount ({chosenDestination?.currency})
                    <input
                      data-testid="payment-fx-destination"
                      inputMode="decimal"
                      autoComplete="off"
                      placeholder="0.00"
                      value={form.destinationAmount}
                      disabled={pending || locked}
                      onChange={(event) => update({ destinationAmount: event.target.value })}
                    />
                  </label>
                ) : null}
                <p className="hint" data-testid="payment-fx-hint">
                  {form.rateMode === 'rate'
                    ? `The rate is ${chosenDestination?.currency} per ${chosenSource?.currency}: the arriving amount is half-up to cents, and both amounts are the money.`
                    : form.rateMode === 'destination'
                      ? `The arriving amount is exact as typed; the rate shown against it is derived, and it never becomes money.`
                      : `These accounts are different currencies: state the rate or the arriving amount. Nothing is assumed.`}
                </p>
              </div>
            ) : (
              <p className="hint" data-testid="payment-same-currency-note">
                Both accounts are {chosenSource?.currency ?? 'the same currency'}, so there is no
                rate to state.
              </p>
            )}

            <label className="field">
              Description
              <input
                data-testid="payment-description"
                autoComplete="off"
                maxLength={500}
                value={form.description}
                disabled={pending || locked}
                onChange={(event) => update({ description: event.target.value })}
              />
            </label>

            <div className="two-fields">
              <label className="field">
                Payment date
                <input
                  type="date"
                  data-testid="payment-date"
                  value={form.transferDate}
                  disabled={pending || locked}
                  onChange={(event) => update({ transferDate: event.target.value })}
                />
              </label>
              <label className="field">
                State
                <select
                  data-testid="payment-status"
                  value={form.status}
                  disabled={pending || locked}
                  onChange={(event) => update({ status: event.target.value as TransferStatus })}
                >
                  <option value="completed">Money has moved</option>
                  <option value="planned">Planned</option>
                </select>
              </label>
            </div>
          </div>
        ) : null}

        {mode === 'complete' ? (
          <div className="editor-fields">
            <label className="field">
              Complete on
              <input
                type="date"
                data-testid="payment-complete-date"
                value={completionDate}
                disabled={pending || locked}
                onChange={(event) => setCompletionDate(event.target.value)}
              />
            </label>
            <p className="hint" data-testid="payment-complete-note">
              One date for the whole pair: both accounts move together on this date, and neither
              leg carries a date of its own.
            </p>
          </div>
        ) : null}

        {mode === 'cancel' ? (
          <p className="hint" data-testid="payment-cancel-note">
            Cancels the whole pair — both legs or neither. This application offers no way to
            change or delete a single leg.
          </p>
        ) : null}

        {props.localError !== null ? (
          <p className="status-bad" role="alert" data-testid="payment-error">
            {props.localError}
          </p>
        ) : null}

        <p
          role="status"
          data-testid="payment-status-line"
          data-save-state={props.saveState}
          className={props.saveState === 'not-saved' || props.saveState === 'unknown' ? 'notice warn' : 'notice'}
          hidden={props.saveState === 'idle'}
        >
          {props.saveMessage === '' ? describeSaveState(props.saveState) : props.saveMessage}
        </p>

        {props.operationKey !== null ? (
          <p className="hint">
            Operation key{' '}
            <code data-testid="payment-operation-key" data-reused={props.operationKey.reused ? 'true' : 'false'}>
              {props.operationKey.key}
            </code>
          </p>
        ) : null}

        {props.previewError !== null ? (
          <p className="status-bad" role="alert" data-testid="payment-preview-error">
            {props.previewError}
          </p>
        ) : null}

        {props.preview !== null ? <PaymentPreview preview={props.preview} /> : null}

        <footer className="editor-footer">
          <button
            type="submit"
            className="primary"
            data-testid="payment-review"
            disabled={pending || locked || props.preview !== null}
          >
            {props.preview !== null ? 'Reviewed' : reviewLabel}
          </button>
          {props.preview !== null ? (
            <button
              type="button"
              data-testid="payment-confirm"
              disabled={pending || locked}
              onClick={props.onConfirm}
            >
              {props.preview.canceled ? 'Confirm the cancellation' : 'Confirm this payment'}
            </button>
          ) : null}
          {reconcile ? (
            <button type="button" data-testid="payment-retry" onClick={props.onReconcile}>
              Reconcile this intent
            </button>
          ) : null}
        </footer>
      </form>
    </div>
  );
}

/**
 * The reviewed preview: both accounts' impacts, the payment's timing, and — when the money
 * lands on a card today — the position against that card's debt.
 */
function PaymentPreview({ preview }: { readonly preview: PaymentPreviewView }) {
  return (
    <section
      className="preview"
      data-testid="payment-preview"
      data-action={preview.action}
      data-status={preview.status}
      data-counted={preview.countedToday ? 'true' : 'false'}
      data-timing={preview.timing}
      aria-label="Payment preview"
    >
      <h3 data-testid="payment-preview-title">
        {preview.canceled ? 'Cancellation preview' : 'Payment preview'}
      </h3>
      <p className="hint">
        <span data-testid="payment-preview-description">{preview.description}</span> ·{' '}
        <span data-testid="payment-preview-date">{preview.transferDate}</span> ·{' '}
        <span data-testid="payment-preview-state">
          {preview.status === 'planned' ? 'planned' : 'completed'}
        </span>{' '}
        · decided on <span data-testid="payment-preview-today">{preview.businessToday}</span>
      </p>

      <ul className="preview-list" data-testid="payment-impacts">
        {preview.impacts.map((impact) => (
          <li
            key={`${impact.accountId}:${impact.side}`}
            data-testid="payment-impact"
            data-side={impact.side}
            data-account-id={impact.accountId}
            data-account-kind={impact.accountKind}
          >
            <span data-testid="payment-impact-account">{impact.accountName}</span>{' '}
            <span className="muted" data-testid="payment-impact-side">
              {impact.side === 'outgoing' ? 'leaves' : 'arrives'}
            </span>{' '}
            <span
              className={`money${impact.legMinor < 0n ? ' negative' : ''}`}
              data-testid="payment-impact-leg"
            >
              {formatMoney(impact.legMinor, impact.currency)}
            </span>{' '}
            · recorded{' '}
            <span className="money" data-testid="payment-impact-before">
              {impact.beforeMinor === null ? 'not read' : formatBalance(impact.beforeMinor, impact.currency)}
            </span>{' '}
            · moves today{' '}
            <span className="money" data-testid="payment-impact-effect">
              {formatMoney(impact.effectMinor, impact.currency)}
            </span>{' '}
            · after{' '}
            <span
              className={`money${impact.afterMinor !== null && impact.afterMinor < 0n ? ' negative' : ''}`}
              data-testid="payment-impact-after"
            >
              {impact.afterMinor === null ? 'not read' : formatBalance(impact.afterMinor, impact.currency)}
            </span>{' '}
            {impact.afterState !== null ? (
              <span data-testid="payment-impact-state" data-card-state={impact.afterState}>
                {impact.afterState}
              </span>
            ) : impact.accountKind === 'card' ? (
              <span data-testid="payment-impact-state" data-card-state="unknown">
                not read
              </span>
            ) : null}
          </li>
        ))}
      </ul>

      <p className="hint" data-testid="payment-timing">
        {paymentTimingLabel(preview.timing)}
        {preview.countedToday ? '' : ' This preview moves no recorded balance today.'}
      </p>

      {preview.positionLabel !== null ? (
        <p className="hint" data-testid="payment-position" data-position={preview.cardPosition ?? ''}>
          <b>{formatMoneyMagnitude(preview.amountMinor, preview.currency)}</b> {preview.positionLabel}
        </p>
      ) : null}

      {/*
        The cross-currency figures (P7-T3): the labeled direction, the six-place figure and its
        approximation marker — all of them the API's own derived read, printed beside the two
        authoritative amounts above rather than in place of them.
      */}
      {preview.fx !== null ? (
        <p
          className="hint"
          data-testid="payment-fx-preview"
          data-input-mode={preview.fx.inputMode}
          data-approximate={preview.fx.approximate ? 'true' : 'false'}
        >
          <span data-testid="payment-fx-direction">{preview.fx.directionLabel}</span> ·{' '}
          <span className="money" data-testid="payment-fx-notation">
            {preview.fx.notation}
          </span>{' '}
          {preview.fx.approximate ? (
            <span data-testid="payment-fx-approximate">(approximate)</span>
          ) : (
            <span data-testid="payment-fx-exact">(exact)</span>
          )}
          <br />
          <span data-testid="payment-fx-note">{preview.fx.note}</span>
          {preview.fx.suppliedRate !== null ? (
            <>
              {' '}
              Supplied rate <span data-testid="payment-fx-supplied">{preview.fx.suppliedRate}</span>.
            </>
          ) : (
            <> No rate was supplied; this one is derived from the two amounts.</>
          )}
        </p>
      ) : null}
    </section>
  );
}
